import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SettingsService } from '@infodata-group/inf-commons';
import { ResizeBoxDirectionType } from '../../types/resize-box-direction.type';

const PREFIX_KEY = 'resize-box-wrapper-';
const SUFIX_KEY_DIRECTION = '-direction';
const SUFIX_KEY_SIZE_A = '-size-a';

/**
 * @Deprecated:: Use `ResizeBoxWrapperComponent` from `@infodata-group/inf-commons-desktop` instead.
 */
@Component({
  selector: 'infl-resize-box-wrapper',
  imports: [CommonModule],
  templateUrl: './resize-box-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './resize-box-wrapper.component.scss',
})
export class ResizeBoxWrapperComponent {
  private readonly settingsService = inject(SettingsService);

  public storageKey = input.required<string>();
  public direction = input<ResizeBoxDirectionType>('vertical');
  protected currentDirection = signal<ResizeBoxDirectionType>('horizontal');

  protected container =
    viewChild.required<ElementRef<HTMLDivElement>>('container');

  public sizeA = input<number>(67);
  private _sizeA = signal<number>(null);
  protected currentSizeA = computed(() => {
    return this._sizeA() !== null ? this._sizeA() : this.sizeA();
  });

  protected styleA = computed(() => {
    if (this.currentDirection() === 'none') {
      return 'width: 100%; height: 100%;';
    }
    if (this.currentDirection() === 'vertical') {
      return `width: ${this.currentSizeA()}%; height: 100%;`;
    }
    return `height: ${this.currentSizeA()}%; width: 100%;`;
  });
  protected styleB = computed(() => {
    if (this.currentDirection() === 'vertical') {
      return `width: ${100 - this.currentSizeA()}%; height: 100%;`;
    }
    return `height: ${100 - this.currentSizeA()}%; width: 100%;`;
  });

  private get storageKeyDirection(): string {
    return `${PREFIX_KEY}${this.storageKey()}${SUFIX_KEY_DIRECTION}`;
  }
  private get storageKeySizeA(): string {
    return `${PREFIX_KEY}${this.storageKey()}${SUFIX_KEY_SIZE_A}`;
  }

  private dragging = false;

  constructor() {
    effect(() => {
      if (this.storageKey()) {
        const direction = this.settingsService.getSetting(
          this.storageKeyDirection,
        );
        if (direction) {
          this.currentDirection.set(direction as ResizeBoxDirectionType);
        }

        const sizeA = this.settingsService.getSetting(this.storageKeySizeA);
        if (sizeA) {
          const parsedSizeA = parseFloat(sizeA);
          if (!isNaN(parsedSizeA)) {
            this._sizeA.set(parsedSizeA);
          }
        }
      }
    });
    effect(() => {
      if (this.direction()) {
        this.currentDirection.set(this.direction());
        this.settingsService.addSetting(
          this.storageKeyDirection,
          this.currentDirection(),
        );
        this.settingsService.save();
      }
    });
  }

  protected onDragStart(event: MouseEvent) {
    event.preventDefault();
    this.dragging = true;
  }

  protected onDrag(event: MouseEvent) {
    if (!this.dragging) {
      return;
    }
    const pos =
      this.currentDirection() === 'vertical' ? event.clientX : event.clientY;
    const rect = this.container().nativeElement.getBoundingClientRect();
    let percent = ((pos - rect.top) / rect.height) * 100;
    if (this.currentDirection() === 'vertical') {
      percent = ((pos - rect.left) / rect.width) * 100;
    }
    percent = Math.max(0, Math.min(100, percent));
    this._sizeA.set(percent);
    this.settingsService.addSetting(this.storageKeySizeA, percent.toString());
    this.settingsService.save();
  }

  protected onDragEnd() {
    this.dragging = false;
  }
}
